<template>
  <div class="calendar-dropdown" ref="dropdown">
    <div class="calendar-dropdown__header" @click.stop="open()">
      <span class="calendar-dropdown__title">{{title}}</span>
      <span class="calendar-dropdown__arrow" :class="{'calendar-dropdown__arrow_disabled': disabled}"></span>
    </div>
    <template v-if="isOpened">
      <div class="calendar-dropdown__overlay"
           @click.stop="close()"></div>
      <ul class="calendar-dropdown__list"
          :class="['calendar-dropdown__list_' + listPosition]"
          :style="listStyle">
        <li v-for="option in options"
            class="calendar-dropdown__item"
            @click.stop="onOptionSelected(option)" :key="option.value">
          <base-check-box v-if="option.value !== 'all' || selectedValues.all !== stateMinus"
                          class="calendar-dropdown__item-checkbox"
                          v-model="selectedValues[option.value]" />
          <base-three-position-check-box v-else
                                         class="calendar-dropdown__item-checkbox"
                                         v-model="selectedValues[option.value]"/>
          <span class="calendar-dropdown__item-title">{{option.label}}</span>
        </li>
      </ul>
    </template>
  </div>
</template>

<script>
/* eslint-disable no-unused-vars */

import _ from 'lodash'
import CalendarHeaderNavPanel from './CalendarHeaderNavPanel'
import Calendar from './index'
import { STATE } from '@/common/BaseThreePositionCheckBox'
/**
 * This component is dropdown of url statuses with possibility to select some of them
 * Included in {@link CalendarHeaderNavPanel}
 */

export default {
  name: 'CalendarDropdownMultiSelect',
  model: {
    prop: 'modelValue',
    event: 'change'
  },
  props: {
    options: {
      type: Array,
      default: () => []
    },
    /**
     * modelValue is this.filters.selectedStatuses in {@link Calendar}
     * */
    modelValue: {
      type: Array,
      default: () => []
    },
    maxHeightList: {
      type: Number
    },
    selectType: {
      type: String
    },
    disabled: {
      type: Boolean,
      default: false
    },
    // statuses that is present on current screen
    filteredOptions: {
      type: Object,
      default: () => {}
    },
    // array of default values, if includes 'all', select all options
    predefinedValue: {
      type: Array,
      default: () => ['all']
    }
  },
  mounted () {
    window.addEventListener('resize', _.debounce(() => {
      this.setListStyle()
    }, 50), false)
  },
  watch: {
    /**
     * callback fires when calendar ui was re-rendered and
     * set of available Publishing statuses was accordingly changed too
     * */
    filteredOptions: {
      handler (value) {
        // according to customer requirements we need to keep url status by changing calendar UI
        // if this logic is changed we need to rewrite this callback
        if (value.filterAction === 'reset') {
          this.selectedOptions = []
          if (this.predefinedValue.includes('all')) {
            this.selectedOptions.push(...this.options.map(item => item.value))
            if (this.selectedOptions.length === this.maxListSize) {
              this.selectedOptions.push('all')
            }
          } else if (!this.predefinedValue.includes('none')) {
            this.selectedOptions = [...this.predefinedValue]
          }
          this.$emit('change', this.selectedOptions)
        }
      },
      deep: true
    }
  },
  data () {
    return {
      isOpened: false,
      stateMinus: STATE.MINUS,
      listStyle: {},
      listPosition: '',
      selectedOptions: []
    }
  },
  methods: {
    clear () {
      this.selectedOptions = []
      this.$emit('change', [])
      this.close()
    },
    open () {
      this.isOpened = true
      this.setListStyle()
    },
    close () {
      this.isOpened = false
    },
    /**
     *  methods {@link #isSelected} and {@link #isDisabled} manage class of specific item in dropdown
     *  pay attention - if classes are applied simultaneously the class with bigger priority wins.
     *  In our case - class that placed in style sheet lower ('calendar-dropdown__item_disabled' in this component)
     * @param option
     * @returns {boolean|default.computed.allSelected|(function(): boolean)}
     */
    isSelected (option) {
      if (option.value === 'all') {
        return this.allSelected
      }
      return this.selectedOptions ? Boolean(this.selectedOptions.includes(option.value)) && Boolean(this.filteredOptions.statuses.includes(option.value)) : false
    },
    isDisabled (value) {
      return !this.filteredOptions.statuses.includes(value) && value !== 'all'
    },
    onOptionSelected (option) {
      if (option.value === 'all') {
        if (this.allSelected || this.selectedValues.all === this.stateMinus) {
          // clear all list
          this.selectedOptions.splice(0, this.selectedOptions.length)
        } else {
          // fill all list
          this.selectedOptions = []
          this.selectedOptions.push(...this.filteredOptions.statuses)
          if (!this.selectedOptions.includes('all')) this.selectedOptions.push('all')
        }
      } else if (this.selectedOptions.includes(option.value)) {
        // remove unselected option
        this.selectedOptions.splice(this.selectedOptions.indexOf(option.value), 1)
      } else {
        // add selected option
        this.selectedOptions.push(option.value)
      }
      this.$emit('change', this.selectedOptions)
    },
    setListStyle () {
      const ref = this.$refs['dropdown']
      if (!ref) {
        return
      }
      const style = {}
      const { top, bottom, left, right } = ref.getBoundingClientRect()
      const topShift = top
      const bottomShift = window.innerHeight - bottom
      let verticalProp, maxHeight
      if (topShift < bottomShift) {
        verticalProp = 'top'
        maxHeight = Math.round(bottomShift) - 30
      } else {
        verticalProp = 'bottom'
        maxHeight = Math.round(top) - 30
      }
      style[verticalProp] = '95%'
      maxHeight = this.maxHeightList ? this.maxHeightList : maxHeight
      style['max-height'] = `${maxHeight}px`
      if (verticalProp === 'top') {
        style['border-top'] = '2px solid #284aff'
        style['border-radius'] = '0 0 8px 8px'
      } else {
        style['border-bottom'] = '2px solid #284aff'
        style['border-radius'] = '8px 8px 0 0'
      }
      const leftShift = left
      const rightShift = window.innerWidth - right
      const horizontalProp = rightShift < leftShift ? 'right' : 'left'
      style[horizontalProp] = 0
      this.listStyle = style
      this.listPosition = verticalProp
    }
  },
  computed: {
    /**
     * we need this tricky way to calculate size because there could be options in selectedOptions
     * that is not present in filteredOptions.statuses because of changing calendar UI after selection
     * */
    size () {
      return this.selectedOptions.reduce((acc, statusId) => {
        if (this.filteredOptions.statuses.includes(statusId)) {
          acc++
        }
        return acc
      }, 0)
    },
    allSelected () {
      return _.difference(this.filteredOptions.statuses, this.selectedOptions).length === 0
    },
    maxListSize () {
      return this.filteredOptions.statuses.length
    },
    selectedValues () {
      const selectedValues = {}
      this.options.forEach(i => { selectedValues[i.value] = false })
      this.selectedOptions.forEach(i => { selectedValues[i] = true })
      if (this.allSelected) {
        selectedValues.all = true
      } else if (!this.selectedOptions.filter(i => i !== 'all').length) {
        selectedValues.all = false
      } else {
        selectedValues.all = STATE.MINUS
      }
      return selectedValues
    },
    /**
     * this property manages title depending on selected options
     */
    title () {
      if (this.size === 0) {
        return 'Page Types'
      }
      let sortedSelectedOptions = this.options.reduce((arr, item) => {
        if (this.selectedOptions.includes(item.value)) {
          arr.push(item.label)
        }
        return arr
      }, [])
      sortedSelectedOptions = sortedSelectedOptions.filter(i => i.toLowerCase() !== 'all')
      return sortedSelectedOptions.length === 1 ? sortedSelectedOptions[0] : sortedSelectedOptions.join(', ')
    }
  }
}
</script>

<style lang="scss">
  .calendar-dropdown {
    position: relative;
    margin-left: 24px;
    width: 235px;
    display: flex;
    min-width: 235px;

    &__title {
      color: $rfx-dark-march;
      text-transform: uppercase;
      font-size: 10px;
      font-weight: 900;
      overflow: hidden;
      line-height: 40px;
      text-overflow: ellipsis;
      width: 180px;
      white-space: nowrap;
    }

    &__overlay {
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      z-index: 9998;
    }

    &__arrow {
      position: absolute;
      right: 15px;
      top: 18px;

      &::after {
        content: '';
        display: block;
        width: 7px;
        height: 7px;
        box-sizing: border-box;
        border-right: 2px solid $rfx-bright-blue;
        border-bottom: 2px solid $rfx-bright-blue;
        transform: rotate(45deg);
        transform-origin: center;
      }

      &_disabled {
        background-color: $rfx-almost-white-lighter;
        display: none;
      }
    }

    &__header {
      display: flex;
      flex-direction: column;
      width: 100%;
      justify-content: space-around;
      border-radius: 4px;
      border: solid 1px rgba(1, 22, 39, 0.1);
      padding-left: 15px;
      padding-right: 40px;
      cursor: pointer;
      height: 40px;

      &:hover {
        background-color: #5671ff38;
      }
    }

    &__list {
      position: absolute;
      z-index: 9999;
      min-width: 100%;
      max-width: 600px;
      padding: 2px 0;
      background: #fff;
      list-style-type: none;
      overflow-x: hidden;
      overflow-y: auto;
      margin: 0 0 1px;
      box-sizing: border-box;

      &_top {
        box-shadow: 0 15px 40px -20px rgba(0, 0, 0, .3);
      }

      &_bottom {
        box-shadow: 0 -15px 40px -20px rgba(0, 0, 0, .3);
      }

      &__shadow {
        box-shadow: 0 0 1px 0 rgba(8, 8, 8, .24), 0 4px 15px 0 rgba(8, 8, 8, .24);
      }
    }

    &__item {
      height: 30px;
      line-height: 30px;
      padding: 5px 12px;
      display: flex;
      cursor: pointer;

      &-checkbox {
        pointer-events: none;
      }
    }

    &__item-title {
      padding-left: 12px;
      @include ellipsis;
      font-size: 12px;
      white-space: initial;
      user-select: none;
      flex: 1 1 100%;

    }
  }

</style>
